import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LockIcon from '@mui/icons-material/Lock'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  fetchCompanyRoles,
  fetchMemberAccess,
  saveMemberAccess,
  type MemberAccess,
  type RbacRole,
} from '../../../../services/rbacService'
import { useRbacStore } from '../../../../stores/rbacStore'
import { addNotification } from '../../../../stores/NotificationStore'
import store from '../../../../stores'
import { useActorPermissionKeys } from '../../../../hooks/usePermission'
import { describeAccessError } from '../../../../utils/rbacMessages'
import {
  accessLockMessage,
  buildAccessPatch,
  countChanges,
  keyState,
  pickerGroups,
  pickerPermissions,
  resetKeys,
  roleDefaultsFor,
  roleOptions,
  rowLockMessage,
  setKeys,
  startDraft,
  switchRole,
  switchRoleOnly,
  toggleKey,
  type AccessDraft,
  type KeyState,
  type PickerPermission,
} from '../../../../utils/memberAccessDraft'

export interface ManageAccessMember {
  userId: string
  name: string
  email?: string
}

interface ManageAccessDrawerProps {
  open: boolean
  companyId: string
  member: ManageAccessMember | null
  /** Used to say "you can't change your own access" before the server has to. */
  currentUserId: string | null
  onClose: () => void
  /** Called with the server's answer after a successful save. */
  onSaved: (access: MemberAccess) => void
}

const STATE_LABEL: Record<KeyState, string> = {
  'role-default': 'Role default',
  allowed: 'Allowed',
  denied: 'Denied',
}

const STATE_HINT: Record<KeyState, string> = {
  'role-default': 'Matches what the role gives.',
  allowed: "Held although the role doesn't give it.",
  denied: 'Not held although the role gives it.',
}

function matchesSearch(permission: PickerPermission, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [permission.key, permission.label, permission.description, permission.group].some((text) =>
    text.toLowerCase().includes(q)
  )
}

/**
 * Manage access for one member (SC-439): the role, and each permission shown against the role's
 * defaults. Save sends only what changed. What the person may change comes from the server's
 * answer for this member; the server still judges the save.
 */
export const ManageAccessDrawer: React.FC<ManageAccessDrawerProps> = ({
  open,
  companyId,
  member,
  currentUserId,
  onClose,
  onSaved,
}) => {
  const catalogue = useRbacStore((s) => s.catalogue)
  const actorPermissions = useActorPermissionKeys()

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [access, setAccess] = useState<MemberAccess | null>(null)
  const [roles, setRoles] = useState<RbacRole[]>([])
  const [draft, setDraft] = useState<AccessDraft | null>(null)
  const [pendingRole, setPendingRole] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const memberId = member?.userId ?? null

  useEffect(() => {
    if (!open || !memberId) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setSaveError(null)
    setAccess(null)
    setDraft(null)
    setPendingRole(null)
    setSearch('')
    setCollapsed(new Set())
    void useRbacStore.getState().ensureCatalogue()

    Promise.all([
      fetchMemberAccess(companyId, memberId),
      // Assigning a role doesn't need roles.view, so a refused list is not a failure: the picker
      // falls back to the system roles from the catalogue.
      fetchCompanyRoles(companyId).catch(() => null),
    ])
      .then(([loaded, list]) => {
        if (cancelled) return
        setAccess(loaded)
        setRoles(list ?? [])
        setDraft(startDraft(loaded))
      })
      .catch((error) => {
        if (!cancelled) setLoadError(describeAccessError(error, useRbacStore.getState().catalogue))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, companyId, memberId])

  const permissions = useMemo(() => pickerPermissions(catalogue), [catalogue])
  const groups = useMemo(() => pickerGroups(catalogue), [catalogue])
  const allKeys = useMemo(() => permissions.map((p) => p.key), [permissions])
  const heldKeys = useMemo(
    () => permissions.filter((p) => p.grantable && p.isActive),
    [permissions]
  )

  const availableRoles = useMemo(() => {
    if (roles.length > 0 || !catalogue) return roles
    return catalogue.systemRoles.map<RbacRole>((r) => ({
      ...r,
      isSystem: true,
      scope: 'global',
    }))
  }, [roles, catalogue])

  const lockContext = useMemo(
    () => ({
      actorPermissions,
      isSelf: !!currentUserId && currentUserId === memberId,
      catalogue,
      roles: availableRoles,
    }),
    [actorPermissions, currentUserId, memberId, catalogue, availableRoles]
  )

  const locks = useMemo(() => {
    const map = new Map<string, string | null>()
    if (!access) return map
    for (const permission of permissions) {
      map.set(permission.key, rowLockMessage(permission, access, lockContext))
    }
    return map
  }, [access, permissions, lockContext])

  if (!member) return null

  const isLocked = (key: string) => !!locks.get(key)
  const roleLock = access ? accessLockMessage('role', access, lockContext) : null
  const permissionsLock = access ? accessLockMessage('permissions', access, lockContext) : null

  const activeRoleKey = draft?.roleKey ?? null
  const defaults =
    access && draft
      ? roleDefaultsFor(activeRoleKey, access, availableRoles, catalogue)
      : new Set<string>()
  const patch =
    access && draft
      ? buildAccessPatch(access, draft, defaults, allKeys, access.canEditPermissions)
      : null
  const counts = draft
    ? countChanges(
        draft,
        defaults,
        heldKeys.map((p) => p.key)
      )
    : { added: 0, removed: 0 }
  const options = access ? roleOptions(access, availableRoles, actorPermissions, catalogue) : []
  const activeRole = options.find((o) => o.key === activeRoleKey)
  const pendingRoleOption = options.find((o) => o.key === pendingRole)

  const visibleKeys = permissions.filter((p) => matchesSearch(p, search)).map((p) => p.key)
  const readOnly = !!access && !access.canEditRole && !access.canEditPermissions

  const update = (next: AccessDraft) => {
    setDraft(next)
    setSaveError(null)
  }

  const handleRoleChange = (roleKey: string) => {
    if (!access || !draft || roleKey === activeRoleKey) return
    if (!access.canEditPermissions) {
      // Only the role can change, and the member's overrides stay: nothing of theirs to rebase.
      update(
        switchRoleOnly(
          draft,
          roleKey,
          roleDefaultsFor(roleKey, access, availableRoles, catalogue),
          access.overrides
        )
      )
      return
    }
    const customised = countChanges(
      draft,
      defaults,
      heldKeys.map((p) => p.key)
    )
    if (customised.added + customised.removed > 0) {
      // Their own changes sit on top of the old role: ask what to do with them.
      setPendingRole(roleKey)
      return
    }
    update(
      switchRole(
        draft,
        roleKey,
        roleDefaultsFor(roleKey, access, availableRoles, catalogue),
        'defaults',
        isLocked,
        allKeys
      )
    )
  }

  const settleRoleChange = (mode: 'keep' | 'defaults' | 'cancel') => {
    if (access && draft && pendingRole && mode !== 'cancel') {
      update(
        switchRole(
          draft,
          pendingRole,
          roleDefaultsFor(pendingRole, access, availableRoles, catalogue),
          mode,
          isLocked,
          allKeys
        )
      )
    }
    setPendingRole(null)
  }

  const handleSave = async () => {
    if (!access || !draft || !patch || !memberId) return
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await saveMemberAccess(companyId, memberId, patch)
      store.dispatch(
        addNotification({
          message: `Access updated for ${member.name}.`,
          type: 'success',
          duration: 5000,
        })
      )
      onSaved(saved)
      onClose()
    } catch (error) {
      setSaveError(describeAccessError(error, catalogue))
    } finally {
      setSaving(false)
    }
  }

  const toggleGroup = (group: string) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (!next.delete(group)) next.add(group)
      return next
    })

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Opened from the users dialog, which is a modal (1300): the drawer has to sit above it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          maxWidth: '100%',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
        'data-testid': 'manage-access-drawer',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Manage access
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {member.name}
          </Typography>
          {member.email && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {member.email}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label="Loading access" />
          </Box>
        )}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {access && draft && (
          <>
            {readOnly && (
              <Alert
                severity="info"
                icon={<LockIcon fontSize="small" />}
                sx={{ mb: 2 }}
                data-testid="access-read-only"
              >
                {permissionsLock ?? roleLock}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Role
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={activeRoleKey ?? ''}
              disabled={!access.canEditRole}
              onChange={(event) => handleRoleChange(event.target.value)}
              SelectProps={{ native: true }}
              inputProps={{ 'aria-label': 'Role', 'data-testid': 'access-role-select' }}
            >
              {access.roleKey === null && (
                <option value="" disabled>
                  No role
                </option>
              )}
              {options.map((option) => (
                <option
                  key={option.key}
                  value={option.key}
                  disabled={!!option.disabledReason && option.key !== activeRoleKey}
                >
                  {option.name}
                  {option.isSystem ? '' : ' (custom)'}
                  {option.disabledReason && option.key !== activeRoleKey
                    ? ` - ${option.disabledReason}`
                    : ''}
                </option>
              ))}
            </TextField>
            {activeRole?.description && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              >
                {activeRole.description}
              </Typography>
            )}
            {!access.canEditRole && !readOnly && roleLock && (
              <Typography
                variant="caption"
                data-testid="access-role-lock"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              >
                {roleLock}
              </Typography>
            )}

            {pendingRole && (
              <Alert severity="warning" sx={{ mt: 2 }} data-testid="access-rebase">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {member.name} has {counts.added + counts.removed} custom change
                  {counts.added + counts.removed === 1 ? '' : 's'} on top of{' '}
                  {activeRole?.name ?? 'the current role'} ({counts.added} added, {counts.removed}{' '}
                  removed). Switch to {pendingRoleOption?.name ?? pendingRole} and:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="contained" onClick={() => settleRoleChange('keep')}>
                    Keep custom changes
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => settleRoleChange('defaults')}
                  >
                    Use {pendingRoleOption?.name ?? 'new role'} defaults
                  </Button>
                  <Button size="small" onClick={() => settleRoleChange('cancel')}>
                    Cancel
                  </Button>
                </Box>
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
              Permissions
            </Typography>
            {!readOnly && !access.canEditPermissions && permissionsLock && (
              <Alert
                severity="info"
                icon={<LockIcon fontSize="small" />}
                sx={{ mb: 1.5 }}
                data-testid="access-permissions-lock"
              >
                {permissionsLock}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <TextField
                size="small"
                placeholder="Search permissions"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                inputProps={{ 'aria-label': 'Search permissions' }}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <Button
                size="small"
                onClick={() => update(setKeys(draft, visibleKeys, true, isLocked))}
              >
                Select all
              </Button>
              <Button
                size="small"
                onClick={() => update(resetKeys(draft, visibleKeys, defaults, isLocked))}
              >
                Reset to role defaults
              </Button>
              <Button
                size="small"
                onClick={() => update(setKeys(draft, visibleKeys, false, isLocked))}
              >
                Clear all
              </Button>
            </Box>
            <Typography
              variant="caption"
              data-testid="access-summary"
              sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
            >
              {draft.granted.size} of {heldKeys.length} granted. {counts.added} added and{' '}
              {counts.removed} removed compared to {activeRole?.name ?? 'the role'}.
              {search ? ' Select all and Clear all apply to the permissions shown.' : ''}
            </Typography>

            {groups.map((group) => {
              const rows = permissions.filter((p) => p.group === group && matchesSearch(p, search))
              if (rows.length === 0) return null
              const changeable = rows.filter((p) => !isLocked(p.key))
              const heldInGroup = changeable.filter((p) => draft.granted.has(p.key)).length
              const allOn = changeable.length > 0 && heldInGroup === changeable.length
              const expanded = !collapsed.has(group)
              return (
                <Box
                  key={group}
                  data-testid={`access-group-${group}`}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    mb: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', pr: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={allOn}
                      indeterminate={heldInGroup > 0 && !allOn}
                      disabled={changeable.length === 0}
                      onChange={() =>
                        update(
                          setKeys(
                            draft,
                            rows.map((p) => p.key),
                            !allOn,
                            isLocked
                          )
                        )
                      }
                      inputProps={{ 'aria-label': `All permissions in ${group}` }}
                    />
                    <Button
                      size="small"
                      color="inherit"
                      aria-expanded={expanded}
                      onClick={() => toggleGroup(group)}
                      startIcon={
                        expanded ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )
                      }
                      sx={{
                        flex: 1,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {group}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        {rows.filter((p) => draft.granted.has(p.key)).length}/{rows.length}
                      </Typography>
                    </Button>
                  </Box>
                  {expanded && (
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {rows.map((permission) => {
                        const lock = locks.get(permission.key) ?? null
                        const state = keyState(permission.key, draft, defaults)
                        const rowReason = lock && lock !== permissionsLock ? lock : null
                        return (
                          <Box
                            component="li"
                            key={permission.key}
                            data-testid={`access-row-${permission.key}`}
                            data-state={state}
                            data-locked={lock ? 'true' : 'false'}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.5,
                              px: 1,
                              py: 0.75,
                              borderTop: 1,
                              borderColor: 'divider',
                              opacity: lock ? 0.75 : 1,
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={draft.granted.has(permission.key)}
                              disabled={!!lock}
                              onChange={() => update(toggleKey(draft, permission.key, isLocked))}
                              inputProps={{ 'aria-label': permission.label }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {permission.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  component="code"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {permission.key}
                                </Typography>
                                {permission.reserved && (
                                  <Chip size="small" variant="outlined" label="Not enforced yet" />
                                )}
                                {state === 'role-default' ? (
                                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                    {STATE_LABEL[state]}
                                  </Typography>
                                ) : (
                                  <Tooltip title={STATE_HINT[state]}>
                                    <Chip
                                      size="small"
                                      label={STATE_LABEL[state]}
                                      color={state === 'allowed' ? 'success' : 'error'}
                                      variant="outlined"
                                      data-testid={`access-state-${permission.key}`}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', display: 'block' }}
                              >
                                {permission.description}
                              </Typography>
                              {rowReason && (
                                <Typography
                                  variant="caption"
                                  data-testid={`access-lock-${permission.key}`}
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {rowReason}
                                </Typography>
                              )}
                            </Box>
                            {lock ? (
                              <Tooltip title={lock}>
                                <LockIcon
                                  fontSize="small"
                                  aria-label={lock}
                                  role="img"
                                  sx={{ color: 'text.disabled', mt: 0.75, mr: 0.5 }}
                                />
                              </Tooltip>
                            ) : (
                              state !== 'role-default' && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    update(resetKeys(draft, [permission.key], defaults, isLocked))
                                  }
                                  aria-label={`Reset ${permission.label} to the role default`}
                                >
                                  Reset
                                </Button>
                              )
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                  )}
                </Box>
              )
            })}
            {visibleKeys.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No permission matches "{search}".
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5 }} data-testid="access-save-error">
            {saveError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
            Every save is written to the audit log.
          </Typography>
          <Button onClick={onClose} variant="outlined">
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {access && !readOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!patch || saving || !!pendingRole}
              data-testid="access-save"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default ManageAccessDrawer
